/**
 * The isolation seam. A single thin wrapper over fetch that:
 *   - resolves group + path → full URL via config/env
 *   - injects `Authorization: Bearer <token>` when a staff token is present
 *   - normalises errors into a typed ApiError
 *
 * Components NEVER call fetch directly and NEVER know the base URL or
 * canonicals. To swap Xano → FastAPI, reimplement this file (and the per-group
 * modules) — components stay untouched.
 */

import { endpoint, type ApiGroup } from "@/config/env";
import { getStaffToken } from "@/lib/session";

export class ApiError extends Error {
  status: number;
  /** Xano-style error code if present (e.g. "accessdenied"). */
  code?: string;
  payload?: unknown;

  constructor(status: number, message: string, code?: string, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.payload = payload;
  }

  get isAuthError() {
    return this.status === 401 || this.code === "accessdenied" || this.status === 403;
  }
}

interface RequestOptions {
  /** Query params; undefined/null entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /** Send the staff bearer token. Default: true for staff group, false otherwise. */
  auth?: boolean;
  signal?: AbortSignal;
}

function buildUrl(group: ApiGroup, path: string, query?: RequestOptions["query"]): string {
  const url = new URL(endpoint(group, path));
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<T>(
  method: string,
  group: ApiGroup,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };

  // Admin-gated groups carry the staff bearer token by default. The jobs/tasks
  // groups are public at the Xano level today but go through the same staff
  // console, so we attach the token too (harmless now, correct if they flip to
  // auth:true later).
  const wantsAuth =
    opts.auth ??
    (group === "staff" ||
      group === "leads" ||
      group === "jobs" ||
      group === "tasks" ||
      group === "apps" ||
      group === "inv" ||
      group === "letters" ||
      group === "dashboard" ||
      group === "quote" ||
      group === "planwire" ||
      group === "feefo");
  if (wantsAuth) {
    const token = getStaffToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let bodyInit: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      // Multipart (e.g. the feasibility-report PDF upload): let the browser set
      // the Content-Type boundary — never set it by hand.
      bodyInit = opts.body;
    } else {
      headers["Content-Type"] = "application/json";
      bodyInit = JSON.stringify(opts.body);
    }
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(group, path, opts.query), {
      method,
      headers,
      body: bodyInit,
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    // Network / CORS failure surfaces here as a TypeError.
    throw new ApiError(0, "Network error — the API could not be reached.", "network", err);
  }

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const payload = data as { message?: string; code?: string } | undefined;
    throw new ApiError(
      res.status,
      payload?.message || res.statusText || `Request failed (${res.status})`,
      payload?.code,
      data,
    );
  }

  return data as T;
}

export const api = {
  get: <T>(group: ApiGroup, path: string, opts?: RequestOptions) =>
    request<T>("GET", group, path, opts),
  post: <T>(group: ApiGroup, path: string, opts?: RequestOptions) =>
    request<T>("POST", group, path, opts),
  patch: <T>(group: ApiGroup, path: string, opts?: RequestOptions) =>
    request<T>("PATCH", group, path, opts),
  delete: <T>(group: ApiGroup, path: string, opts?: RequestOptions) =>
    request<T>("DELETE", group, path, opts),
};
